import styles from './SelectInput.module.scss';
import {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { FormFieldContext } from '../form/FormFieldContext';
import { FormContext } from '../form/FormContext';
import { FormFieldInputContext } from '../form/FormField';
import { InputContainer } from './InputContainer';

interface Props {
  children: ReactNode;
  defaultValue?: string;
  disabled?: boolean;
  end?: ReactNode;
  id?: string;
  label: string;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  onValue?: (value: string) => void;
  required?: boolean;
  value?: string;
}

export function SelectInput({
  children,
  defaultValue,
  disabled,
  label,
  required,
  value,
  ...props
}: Props) {
  const selectRef = useRef<HTMLSelectElement>(null);
  const formContext = useContext(FormContext);
  const formFieldContext = useContext(FormFieldContext);
  const formFieldInputContext = useContext(FormFieldInputContext);
  const name = props.name ?? formFieldContext.name;

  useEffect(() => {
    if (name && selectRef.current) {
      const prev = formContext.inputs.get(name) ?? [];
      formContext.inputs.set(name, [...prev, selectRef.current]);
      formFieldInputContext?.set(name, selectRef.current);
    }
    return () => {
      formContext.inputs.delete(name);
    };
  }, [formContext, name, formFieldInputContext]);

  function onInputClick() {
    if (selectRef.current) {
      selectRef.current.focus();
      selectRef.current.click();
    }
  }

  function onChange(event: ChangeEvent<HTMLSelectElement>) {
    props.onChange?.(event);
    props.onValue?.(event.currentTarget.value);
  }

  return (
    <InputContainer
      addon={props.end}
      id={props.id}
      label={label}
      onClick={onInputClick}
    >
      {({ id }) => (
        <select
          ref={selectRef}
          className={styles['select-input-element']}
          defaultValue={defaultValue}
          disabled={disabled}
          id={id}
          name={name}
          onChange={onChange}
          required={required}
          value={value}
        >
          {children}
        </select>
      )}
    </InputContainer>
  );
}
